import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";

/**
 * PostcodeCombobox — typable input + chevron dropdown styled like the rest of the
 * qb-input selects on the Customer & Job form. Replaces the native <input list="datalist">
 * which couldn't be styled consistently.
 */
export function PostcodeCombobox({ value, onChange, postcodes = [], depot = "" }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const matches = useMemo(() => {
    const q = (value || "").trim().toUpperCase();
    let list = postcodes;
    if (depot) list = list.filter((p) => !p.depot || p.depot === depot);
    if (q) list = list.filter((p) => (p.code || "").toUpperCase().includes(q));
    // alphabetical
    return [...list].sort((a, b) => (a.code || "").localeCompare(b.code || ""));
  }, [postcodes, value, depot]);

  return (
    <div className="relative" ref={wrapRef}>
      <input
        value={value || ""}
        onChange={(e) => { onChange(e.target.value.toUpperCase()); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === "Escape") setOpen(false);
          if (e.key === "Enter" && matches.length > 0) {
            e.preventDefault();
            onChange(matches[0].code);
            setOpen(false);
          }
        }}
        className="qb-input font-mono pr-10"
        data-testid="postcode-input"
        placeholder="Type or pick…"
        autoComplete="off"
      />
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="absolute right-0 top-0 h-full w-9 inline-flex items-center justify-center text-slate-400 hover:text-brand-blue"
        aria-label="Toggle postcode list"
        tabIndex={-1}
        data-testid="postcode-toggle"
      >
        <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          className="absolute z-30 mt-1 left-0 right-0 max-h-64 overflow-auto bg-white border border-slate-300 rounded-md shadow-lg"
          data-testid="postcode-suggestions"
        >
          {matches.length === 0 && (
            <div className="px-3 py-2 text-xs text-slate-500">
              {value ? `No saved postcode matches "${value}". Keep typing — it'll save with the quote.` : "No postcodes configured."}
            </div>
          )}
          {matches.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => { onChange(p.code); setOpen(false); }}
              className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 focus:bg-blue-50 outline-none font-mono flex items-center justify-between gap-2"
              data-testid={`postcode-suggestion-${p.code}`}
            >
              <span>{p.code}</span>
              <span className="text-[10px] uppercase tracking-wider text-slate-500">
                {p.depot ? `${p.depot} · ` : ""}{p.travel_time_hours}h
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
